import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHelper, ScopeOfPracticeEnum } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import { FacilitySearchDto, PagedResultDto } from '../dtos';
import {
  FacilityClassification,
  FacilityDto,
  FacilityStatus
} from '@umss/shared/data-access';

@Injectable({
  providedIn: 'root'
})
export class FacilityService {
  private readonly apiUrl = '/api/facilities';

  constructor(private http: HttpClient) {}

  /**
   * Search facilities with filters and pagination (Contractor View)
   * Uses the contractor-view endpoint which includes distance and favorites
   * Falls back to mock data if API fails
   */
  searchFacilities(
    searchDto: FacilitySearchDto
  ): Observable<PagedResultDto<FacilityDto>> {
    const params = HttpHelper.toHttpParams(searchDto);

    return this.http.get<PagedResultDto<FacilityDto>>(
      `${this.apiUrl}/contractor-view`,
      {
        params
      }
    );
  }

  /**
   * Get facility by ID (Contractor View)
   * Returns facility details with isFavorite status
   */
  getFacilityById(id: string): Observable<FacilityDto> {
    return this.http.get<FacilityDto>(`${this.apiUrl}/${id}/contractor-view`);
  }

  /**
   * Add facility to contractor's favorites
   */
  addFavorite(facilityId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/${facilityId}/contractor-favorite`,
      {}
    );
  }

  /**
   * Remove facility from contractor's favorites
   */
  removeFavorite(facilityId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${facilityId}/contractor-favorite`
    );
  }

  /**
   * Mock data fallback for development/testing
   */
  private getMockFacilities(
    searchDto: FacilitySearchDto
  ): PagedResultDto<FacilityDto> {
    let filtered = [...MOCK_FACILITIES];

    // Apply search term filter
    if (searchDto.searchTerm) {
      const searchLower = searchDto.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (facility) =>
          facility.name.toLowerCase().includes(searchLower) ||
          facility.location?.city?.toLowerCase().includes(searchLower) ||
          facility.location?.stateCode?.toLowerCase().includes(searchLower)
      );
    }

    // Apply classification filter
    if (searchDto.classification !== undefined) {
      filtered = filtered.filter(
        (facility) => facility.classification === searchDto.classification
      );
    }

    // Apply state filter
    if (searchDto.stateCode) {
      filtered = filtered.filter(
        (facility) =>
          facility.location?.stateCode?.toLowerCase() ===
          searchDto.stateCode?.toLowerCase()
      );
    }

    // Pagination
    const pageNumber = searchDto.pageNumber || 1;
    const pageSize = searchDto.pageSize || 10;
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      totalCount: filtered.length,
      pageNumber,
      pageSize,
      totalPages: Math.ceil(filtered.length / pageSize),
      hasNextPage: endIndex < filtered.length,
      hasPreviousPage: pageNumber > 1
    };
  }

  /**
   * Get mock facility by ID
   */
  private getMockFacilityById(id: string): FacilityDto {
    const facility = MOCK_FACILITIES.find((f) => f.id === id);
    if (facility) {
      return facility;
    }
    // Return first mock facility as fallback
    return MOCK_FACILITIES[0];
  }
}

// Mock data for development/testing
const MOCK_FACILITIES: FacilityDto[] = [
  {
    id: '1',
    name: 'St. Judes Hospital',
    description: 'A leading healthcare facility',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.STANDARD,
    classificationDisplay: 'Standard',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 1,
      addressLine1: '123 Medical Center Dr',
      city: 'Midvale',
      stateCode: 'UT',
      zipCode: '84047',
      countryCode: 'USA',
      latitude: 40.6119,
      longitude: -111.8997,
      fullAddress: '123 Medical Center Dr, Midvale, UT 84047'
    },
    contactPhone: '(801) 555-0100',
    contactEmail: 'info@stjudes.example.com',
    onboardingCompleted: true,
    supportedScopes: [
      ScopeOfPracticeEnum.Rn,
      ScopeOfPracticeEnum.Cna,
      ScopeOfPracticeEnum.Lpn
    ],
    contacts: [],
    distanceMiles: 3.9,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '2',
    name: 'City Hospital',
    description: 'Community healthcare center',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.INTERMEDIATE,
    classificationDisplay: 'Intermediate',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 2,
      addressLine1: '456 Health Ave',
      city: 'Salt Lake City',
      stateCode: 'UT',
      zipCode: '84101',
      countryCode: 'USA',
      latitude: 40.7608,
      longitude: -111.891,
      fullAddress: '456 Health Ave, Salt Lake City, UT 84101'
    },
    contactPhone: '(801) 555-0200',
    contactEmail: 'info@cityhospital.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 2.5,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '3',
    name: 'Alpine Valley Hospital',
    description: 'Mountain region healthcare',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.INTERMEDIATE,
    classificationDisplay: 'Expert',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 3,
      addressLine1: '789 Alpine Rd',
      city: 'Provo',
      stateCode: 'UT',
      zipCode: '84601',
      countryCode: 'USA',
      latitude: 40.2338,
      longitude: -111.6585,
      fullAddress: '789 Alpine Rd, Provo, UT 84601'
    },
    contactPhone: '(801) 555-0300',
    contactEmail: 'info@alpinevalley.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 5.2,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '4',
    name: 'Mountain View Hospital',
    description: 'Scenic healthcare facility',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.STANDARD,
    classificationDisplay: 'Standard',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 4,
      addressLine1: '321 Vista Blvd',
      city: 'Payson',
      stateCode: 'UT',
      zipCode: '84651',
      countryCode: 'USA',
      latitude: 40.0444,
      longitude: -111.7324,
      fullAddress: '321 Vista Blvd, Payson, UT 84651'
    },
    contactPhone: '(801) 555-0400',
    contactEmail: 'info@mountainview.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 1.8,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '5',
    name: 'Timpanogos Regional',
    description: 'Regional medical center',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.INTERMEDIATE,
    classificationDisplay: 'Intermediate',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 5,
      addressLine1: '555 Regional Way',
      city: 'Orem',
      stateCode: 'UT',
      zipCode: '84057',
      countryCode: 'USA',
      latitude: 40.2969,
      longitude: -111.6946,
      fullAddress: '555 Regional Way, Orem, UT 84057'
    },
    contactPhone: '(801) 555-0500',
    contactEmail: 'info@timpanogos.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 6.4,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '6',
    name: 'Valley Medical Center',
    description: 'Comprehensive care facility',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 2,
    facilityTypeName: 'Clinic',
    facilityTypeHasSubtypes: false,
    classification: FacilityClassification.STANDARD,
    classificationDisplay: 'Standard',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 6,
      addressLine1: '100 Valley Dr',
      city: 'Sandy',
      stateCode: 'UT',
      zipCode: '84070',
      countryCode: 'USA',
      latitude: 40.5649,
      longitude: -111.859,
      fullAddress: '100 Valley Dr, Sandy, UT 84070'
    },
    contactPhone: '(801) 555-0600',
    contactEmail: 'info@valleymedical.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 4.1,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '7',
    name: 'Wasatch Care Center',
    description: 'Specialized care services',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 3,
    facilityTypeName: 'Nursing Home',
    facilityTypeHasSubtypes: false,
    classification: FacilityClassification.EXPERT,
    classificationDisplay: 'Expert',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 7,
      addressLine1: '200 Wasatch Blvd',
      city: 'Draper',
      stateCode: 'UT',
      zipCode: '84020',
      countryCode: 'USA',
      latitude: 40.5246,
      longitude: -111.8638,
      fullAddress: '200 Wasatch Blvd, Draper, UT 84020'
    },
    contactPhone: '(801) 555-0700',
    contactEmail: 'info@wasatchcare.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 3.2,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '8',
    name: 'Jordan Valley Hospital',
    description: 'Full-service hospital',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.INTERMEDIATE,
    classificationDisplay: 'Intermediate',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 8,
      addressLine1: '300 Jordan Way',
      city: 'West Jordan',
      stateCode: 'UT',
      zipCode: '84088',
      countryCode: 'USA',
      latitude: 40.6097,
      longitude: -111.9391,
      fullAddress: '300 Jordan Way, West Jordan, UT 84088'
    },
    contactPhone: '(801) 555-0800',
    contactEmail: 'info@jordanvalley.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 2.8,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '9',
    name: 'University Health Center',
    description: 'Academic medical center',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 1,
    facilityTypeName: 'Hospital',
    facilityTypeHasSubtypes: true,
    classification: FacilityClassification.EXPERT,
    classificationDisplay: 'Expert',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 9,
      addressLine1: '400 University Ave',
      city: 'Salt Lake City',
      stateCode: 'UT',
      zipCode: '84112',
      countryCode: 'USA',
      latitude: 40.7649,
      longitude: -111.8421,
      fullAddress: '400 University Ave, Salt Lake City, UT 84112'
    },
    contactPhone: '(801) 555-0900',
    contactEmail: 'info@uhealth.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 7.5,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '10',
    name: 'Primary Care Clinic',
    description: 'Family medicine clinic',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 2,
    facilityTypeName: 'Clinic',
    facilityTypeHasSubtypes: false,
    classification: FacilityClassification.STANDARD,
    classificationDisplay: 'Standard',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 10,
      addressLine1: '500 Main St',
      city: 'Murray',
      stateCode: 'UT',
      zipCode: '84107',
      countryCode: 'USA',
      latitude: 40.6669,
      longitude: -111.888,
      fullAddress: '500 Main St, Murray, UT 84107'
    },
    contactPhone: '(801) 555-1000',
    contactEmail: 'info@primarycare.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 1.5,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  },
  {
    id: '11',
    name: 'Cottonwood Medical',
    description: 'Specialty medical services',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    facilityTypeId: 2,
    facilityTypeName: 'Clinic',
    facilityTypeHasSubtypes: false,
    classification: FacilityClassification.INTERMEDIATE,
    classificationDisplay: 'Intermediate',
    status: FacilityStatus.Active,
    statusDisplay: 'Active',
    location: {
      id: 11,
      addressLine1: '600 Cottonwood Ln',
      city: 'Holladay',
      stateCode: 'UT',
      zipCode: '84117',
      countryCode: 'USA',
      latitude: 40.6599,
      longitude: -111.8246,
      fullAddress: '600 Cottonwood Ln, Holladay, UT 84117'
    },
    contactPhone: '(801) 555-1100',
    contactEmail: 'info@cottonwood.example.com',
    onboardingCompleted: true,
    supportedScopes: [ScopeOfPracticeEnum.Rn, ScopeOfPracticeEnum.Cna],
    contacts: [],
    distanceMiles: 4.8,
    isFavorite: true,
    contractSignedDate: undefined,
    corporationId: undefined,
    corporationName: undefined,
    timeZone: 'America/Denver',
    billingManagedByCorporate: false
  }
];
